import React, { useState, useEffect } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "./utils/api";
import Layout from "./components/Layout";

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function Input() {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkDate, setBulkDate] = useState("");
  const [bulkEntries, setBulkEntries] = useState([]);
  const [form, setForm] = useState({
    date: getTodayDate(),
    category: "",
    value: "",
    currency: "",
    notes: "",
  });
  const [editId, setEditId] = useState(null);
  const [editingInlineId, setEditingInlineId] = useState(null);
  const [inlineEditForm, setInlineEditForm] = useState({
    date: "",
    category: "",
    value: "",
    currency: "",
    notes: "",
  });

  const fetchEntries = () => {
    apiGet("/wealth_entries")
      .then((res) => res.json())
      .then(setEntries);
  };

  const fetchCategories = () => {
    apiGet("/categories")
      .then((res) => res.json())
      .then(setCategories);
  };

  const fetchCurrencies = () => {
    apiGet("/currencies")
      .then((res) => res.json())
      .then(setCurrencies);
  };

  const fetchLastMonthEntries = () => {
    // Get the last month's entries to pre-fill bulk form
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthStr = lastMonth.toISOString().slice(0, 7); // YYYY-MM

    apiGet("/wealth_entries")
      .then((res) => res.json())
      .then((allEntries) => {
        const filtered = allEntries.filter((e) => e.date.startsWith(lastMonthStr));
        // Group by category
        const byCategory = {};
        filtered.forEach((e) => {
          byCategory[e.category] = e;
        });

        // Initialize bulk entries with all categories
        // lastValue stores the previous month's value for placeholder display
        const bulk = categories.map((cat) => ({
          category: cat.name,
          value: "",  // Empty - user must enter new value
          lastValue: byCategory[cat.name]?.value || null,  // For placeholder
          currency: byCategory[cat.name]?.currency || (currencies[0]?.code || "CHF"),
          notes: "",  // Empty - user can enter new notes
          saved: false,  // Track if this entry has been saved
          saving: false, // Track if currently saving
        }));
        setBulkEntries(bulk);
      });
  };

  useEffect(() => {
    fetchEntries();
    fetchCategories();
    fetchCurrencies();
  }, []);

  useEffect(() => {
    // When categories are loaded and bulk mode is enabled, fetch last month's data
    if (isBulkMode && categories.length > 0 && currencies.length > 0) {
      fetchLastMonthEntries();
    }
  }, [isBulkMode, categories, currencies]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      value: Number(form.value),
    };
    if (editId) {
      apiPut(`/wealth_entries/${editId}`, payload).then(() => {
        fetchEntries();
        setForm({
          date: getTodayDate(),
          category: categories[0]?.name || "",
          value: "",
          currency: currencies[0]?.code || "",
          notes: "",
        });
        setEditId(null);
      });
    } else {
      apiPost("/wealth_entries", payload).then(() => {
        fetchEntries();
        setForm({
          date: getTodayDate(),
          category: categories[0]?.name || "",
          value: "",
          currency: currencies[0]?.code || "",
          notes: "",
        });
      });
    }
  };

  const handleBulkSave = () => {
    if (!bulkDate) {
      alert("Bitte wählen Sie ein Datum aus.");
      return;
    }

    const promises = bulkEntries
      .filter((entry) => entry.value && entry.value !== "")
      .map((entry) => {
        return apiPost("/wealth_entries", {
          date: bulkDate,
          category: entry.category,
          value: Number(entry.value),
          currency: entry.currency,
          notes: entry.notes,
        });
      });

    Promise.all(promises).then(() => {
      fetchEntries();
      setIsBulkMode(false);
      setBulkDate("");
      setBulkEntries([]);
    });
  };

  const updateBulkEntry = (index, field, value) => {
    const updated = [...bulkEntries];
    updated[index][field] = value;
    setBulkEntries(updated);
  };

  const copyLastMonthValues = () => {
    const updated = bulkEntries.map((entry) => ({
      ...entry,
      value: entry.lastValue !== null ? String(entry.lastValue) : entry.value,
    }));
    setBulkEntries(updated);
  };

  const saveSingleEntry = (index) => {
    const entry = bulkEntries[index];
    if (!bulkDate) {
      alert("Bitte wähle zuerst ein Datum.");
      return;
    }
    if (!entry.value || entry.value === "") {
      alert("Bitte gib einen Wert ein.");
      return;
    }

    // Mark as saving
    const updatedSaving = [...bulkEntries];
    updatedSaving[index].saving = true;
    setBulkEntries(updatedSaving);

    apiPost("/wealth_entries", {
      date: bulkDate,
      category: entry.category,
      value: Number(entry.value),
      currency: entry.currency,
      notes: entry.notes,
    }).then(() => {
      // Mark as saved
      const updatedSaved = [...bulkEntries];
      updatedSaved[index].saving = false;
      updatedSaved[index].saved = true;
      setBulkEntries(updatedSaved);
      fetchEntries();
    });
  };

  const handleEdit = (entry) => {
    setEditId(entry.id);
    setForm({
      date: entry.date,
      category: entry.category,
      value: entry.value,
      currency: entry.currency,
      notes: entry.notes,
    });
  };

  const handleInlineEditClick = (entry) => {
    setEditingInlineId(entry.id);
    setInlineEditForm({
      date: entry.date,
      category: entry.category,
      value: entry.value,
      currency: entry.currency,
      notes: entry.notes || "",
    });
    // Prevent body scroll on mobile when modal opens
    if (window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    }
  };

  const handleCancelInlineEdit = () => {
    setEditingInlineId(null);
    setInlineEditForm({
      date: "",
      category: "",
      value: "",
      currency: "",
      notes: "",
    });
    // Re-enable body scroll
    document.body.style.overflow = '';
  };

  const handleSaveInlineEdit = (e) => {
    e.preventDefault();
    apiPut(`/wealth_entries/${editingInlineId}`, {
      ...inlineEditForm,
      value: Number(inlineEditForm.value),
    })
      .then((res) => res.json())
      .then(() => {
        fetchEntries();
        handleCancelInlineEdit();
      })
      .catch((err) => console.error("Fehler beim Speichern:", err));
  };

  const handleDelete = (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    apiDelete(`/wealth_entries/${id}`).then(() => {
      fetchEntries();
      if (editId === id) {
        setEditId(null);
        setForm({
          date: getTodayDate(),
          category: categories[0]?.name || "",
          value: "",
          currency: currencies[0]?.code || "",
          notes: "",
        });
      }
    });
  };

  // Filter entries: show only current and last month
  const getRecentEntries = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const lastMonth = (currentMonth - 1 + 12) % 12;
    const currentYear = today.getFullYear();

    return entries.filter((entry) => {
      const entryDate = new Date(entry.date);
      const entryMonth = entryDate.getMonth();
      const entryYear = entryDate.getFullYear();
      return (
        (entryYear === currentYear && entryMonth === currentMonth) ||
        (entryYear === currentYear && entryMonth === lastMonth)
      );
    });
  };

  const recentEntries = getRecentEntries();

  return (
    <Layout>
      <div className="card">
        <div className="flex-between mb-md">
          <h3 className="card-header" style={{ marginBottom: 0 }}>
            {isBulkMode ? "Monatliche Erfassung (Alle Kategorien)" : editId ? "Eintrag bearbeiten" : "Neuer Eintrag"}
          </h3>
          <button
            className={`btn ${isBulkMode ? 'btn-secondary' : 'btn-primary'}`}
            onClick={() => setIsBulkMode(!isBulkMode)}
          >
            {isBulkMode ? "Einzelerfassung" : "Monatliche Erfassung"}
          </button>
        </div>

        {isBulkMode ? (
          <div>
            <div className="flex gap-md mb-lg" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Datum (Monat)</label>
                <input
                  type="date"
                  value={bulkDate}
                  onChange={(e) => setBulkDate(e.target.value)}
                  required
                  style={{ maxWidth: '200px' }}
                />
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={copyLastMonthValues}
                disabled={!bulkEntries.some((e) => e.lastValue !== null)}
              >
                Werte vom Vormonat übernehmen
              </button>
            </div>

            {/* Desktop: Table View */}
            <div className="hidden md:block" style={{ marginBottom: 'var(--space-large)' }}>
              {/* Progress indicator */}
              <div className="mb-md" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                  Fortschritt: {bulkEntries.filter(e => e.saved).length} / {bulkEntries.length}
                </span>
                <div style={{
                  flex: 1,
                  height: '8px',
                  backgroundColor: 'var(--border-color)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(bulkEntries.filter(e => e.saved).length / bulkEntries.length) * 100}%`,
                    height: '100%',
                    backgroundColor: 'var(--success-color, #22c55e)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
              <table className="table-wrapper" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Kategorie</th>
                    <th>Vormonat</th>
                    <th>Neuer Wert</th>
                    <th>Währung</th>
                    <th>Notizen</th>
                    <th style={{ width: '120px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Unsaved entries first */}
                  {bulkEntries.filter(e => !e.saved).map((entry, filteredIndex) => {
                    const originalIndex = bulkEntries.findIndex(e => e.category === entry.category);
                    return (
                      <tr key={originalIndex}>
                        <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                          {filteredIndex + 1}
                        </td>
                        <td style={{ fontWeight: 500 }}>{entry.category}</td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                          {entry.lastValue !== null
                            ? Number(entry.lastValue).toLocaleString('de-CH')
                            : '-'}
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            value={entry.value}
                            onChange={(e) => updateBulkEntry(originalIndex, 'value', e.target.value)}
                            className="input"
                            style={{ width: '140px' }}
                            placeholder="0.00"
                          />
                        </td>
                        <td>
                          <select
                            value={entry.currency}
                            onChange={(e) => updateBulkEntry(originalIndex, 'currency', e.target.value)}
                            className="input"
                            style={{ width: '90px' }}
                          >
                            {currencies.map((cur) => (
                              <option key={cur.id} value={cur.code}>
                                {cur.code}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={entry.notes}
                            onChange={(e) => updateBulkEntry(originalIndex, 'notes', e.target.value)}
                            className="input"
                            style={{ width: '150px' }}
                            placeholder="Optional"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-success"
                            onClick={() => saveSingleEntry(originalIndex)}
                            disabled={entry.saving || !entry.value}
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                          >
                            {entry.saving ? '...' : 'Speichern'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Saved entries at the bottom */}
                  {bulkEntries.filter(e => e.saved).map((entry) => {
                    const originalIndex = bulkEntries.findIndex(e => e.category === entry.category);
                    return (
                      <tr key={originalIndex} style={{
                        backgroundColor: 'var(--success-bg, #f0fdf4)',
                        opacity: 0.7
                      }}>
                        <td style={{ textAlign: 'center', color: 'var(--success-color, #22c55e)' }}>
                          ✓
                        </td>
                        <td style={{ fontWeight: 500 }}>{entry.category}</td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                          {entry.lastValue !== null
                            ? Number(entry.lastValue).toLocaleString('de-CH')
                            : '-'}
                        </td>
                        <td style={{ fontWeight: 500 }}>
                          {Number(entry.value).toLocaleString('de-CH')}
                        </td>
                        <td>{entry.currency}</td>
                        <td style={{ color: 'var(--text-secondary)' }}>{entry.notes || '-'}</td>
                        <td style={{ color: 'var(--success-color, #22c55e)', fontSize: '13px' }}>
                          Gespeichert
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile: Card View */}
            <div className="md:hidden" style={{ marginBottom: 'var(--space-large)' }}>
              {/* Mobile Progress indicator */}
              <div className="mb-md" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                  {bulkEntries.filter(e => e.saved).length} / {bulkEntries.length}
                </span>
                <div style={{
                  flex: 1,
                  height: '8px',
                  backgroundColor: 'var(--border-color)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(bulkEntries.filter(e => e.saved).length / bulkEntries.length) * 100}%`,
                    height: '100%',
                    backgroundColor: 'var(--success-color, #22c55e)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              {/* Unsaved entries */}
              {bulkEntries.filter(e => !e.saved).map((entry) => {
                const originalIndex = bulkEntries.findIndex(e => e.category === entry.category);
                return (
                  <div key={originalIndex} className="card-body bg-secondary-50 dark:bg-secondary-700 rounded-lg p-4 mb-3">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 className="font-semibold text-secondary-900 dark:text-secondary-100 text-base" style={{ margin: 0 }}>
                        {entry.category}
                      </h4>
                      {entry.lastValue !== null && (
                        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                          Vormonat: {Number(entry.lastValue).toLocaleString('de-CH')}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="form-group">
                        <label className="label text-xs">Neuer Wert</label>
                        <input
                          type="number"
                          step="0.01"
                          value={entry.value}
                          onChange={(e) => updateBulkEntry(originalIndex, 'value', e.target.value)}
                          className="input w-full"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="form-group">
                        <label className="label text-xs">Währung</label>
                        <select
                          value={entry.currency}
                          onChange={(e) => updateBulkEntry(originalIndex, 'currency', e.target.value)}
                          className="input w-full"
                        >
                          {currencies.map((cur) => (
                            <option key={cur.id} value={cur.code}>
                              {cur.code}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="form-group mt-3">
                      <input
                        type="text"
                        value={entry.notes}
                        onChange={(e) => updateBulkEntry(originalIndex, 'notes', e.target.value)}
                        className="input w-full"
                        placeholder="Notizen (optional)"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-success w-full mt-3"
                      onClick={() => saveSingleEntry(originalIndex)}
                      disabled={entry.saving || !entry.value}
                    >
                      {entry.saving ? 'Speichern...' : 'Speichern'}
                    </button>
                  </div>
                );
              })}

              {/* Saved entries (collapsed) */}
              {bulkEntries.filter(e => e.saved).length > 0 && (
                <div style={{
                  backgroundColor: 'var(--success-bg, #f0fdf4)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginTop: '16px'
                }}>
                  <div style={{ fontWeight: 500, color: 'var(--success-color, #22c55e)', marginBottom: '8px' }}>
                    ✓ Gespeichert ({bulkEntries.filter(e => e.saved).length})
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {bulkEntries.filter(e => e.saved).map(e => e.category).join(', ')}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-md">
              <button className="btn btn-success" onClick={handleBulkSave}>
                Alle speichern
              </button>
              <button className="btn btn-secondary" onClick={() => setIsBulkMode(false)}>
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Datum</label>
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Kategorie</label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  required
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Wert</label>
                <input
                  type="number"
                  name="value"
                  value={form.value}
                  onChange={handleChange}
                  required
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Währung</label>
                <select
                  name="currency"
                  value={form.currency}
                  onChange={handleChange}
                  required
                >
                  {currencies.map((cur) => (
                    <option key={cur.id} value={cur.code}>
                      {cur.code}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Notizen</label>
                <input
                  type="text"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="flex gap-md">
              <button type="submit" className="btn btn-primary">
                {editId ? "Aktualisieren" : "Hinzufügen"}
              </button>
              {editId && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditId(null);
                    setForm({
                      date: getTodayDate(),
                      category: categories[0]?.name || "",
                      value: "",
                      currency: currencies[0]?.code || "",
                      notes: "",
                    });
                  }}
                >
                  Abbrechen
                </button>
              )}
            </div>
          </form>
        )}
      </div>

      <h2>Aktuelle Einträge (letzter & aktueller Monat)</h2>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Kategorie</th>
              <th>Wert</th>
              <th>Währung</th>
              <th>Notizen</th>
            </tr>
          </thead>
          <tbody>
            {recentEntries.length === 0 ? (
              <tr>
                <td colSpan={5} className="no-entries">
                  Keine Einträge vorhanden.
                </td>
              </tr>
            ) : (
              recentEntries.map((entry) => (
                <React.Fragment key={entry.id}>
                  {editingInlineId === entry.id ? (
                    <>
                      {/* Mobile: Full-Screen Modal */}
                      <tr className="md:hidden">
                        <td colSpan={5} style={{ padding: 0 }}>
                          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-50 flex items-start justify-center overflow-y-auto p-4">
                            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-lg my-8">
                              <div className="sticky top-0 bg-secondary-700 dark:bg-secondary-800 text-white px-6 py-4 rounded-t-lg flex items-center justify-between border-b border-secondary-600 dark:border-secondary-700">
                                <h3 className="text-lg font-semibold">Eintrag bearbeiten</h3>
                                <button
                                  type="button"
                                  onClick={handleCancelInlineEdit}
                                  className="text-white hover:bg-secondary-600 dark:hover:bg-secondary-700 rounded-full p-2 transition-colors"
                                >
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </div>
                              <form onSubmit={handleSaveInlineEdit} className="p-6">
                                <div className="space-y-4">
                                  <div className="form-group">
                                    <label className="label">Datum</label>
                                    <input
                                      type="date"
                                      value={inlineEditForm.date}
                                      onChange={(e) => setInlineEditForm({ ...inlineEditForm, date: e.target.value })}
                                      required
                                      className="input"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label className="label">Kategorie</label>
                                    <select
                                      value={inlineEditForm.category}
                                      onChange={(e) => setInlineEditForm({ ...inlineEditForm, category: e.target.value })}
                                      required
                                      className="input"
                                    >
                                      <option value="">Wählen...</option>
                                      {categories.map((cat) => (
                                        <option key={cat.id} value={cat.name}>
                                          {cat.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="form-group">
                                    <label className="label">Wert</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={inlineEditForm.value}
                                      onChange={(e) => setInlineEditForm({ ...inlineEditForm, value: e.target.value })}
                                      required
                                      className="input"
                                      placeholder="0.00"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label className="label">Währung</label>
                                    <select
                                      value={inlineEditForm.currency}
                                      onChange={(e) => setInlineEditForm({ ...inlineEditForm, currency: e.target.value })}
                                      required
                                      className="input"
                                    >
                                      <option value="">Wählen...</option>
                                      {currencies.map((cur) => (
                                        <option key={cur.id} value={cur.code}>
                                          {cur.code}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="form-group">
                                    <label className="label">Notizen (optional)</label>
                                    <textarea
                                      value={inlineEditForm.notes || ""}
                                      onChange={(e) => setInlineEditForm({ ...inlineEditForm, notes: e.target.value })}
                                      rows="3"
                                      className="input"
                                      placeholder="Notizen..."
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-3 mt-6">
                                  <button type="submit" className="btn btn-primary flex-1">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="inline mr-2">
                                      <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Speichern
                                  </button>
                                  <button type="button" className="btn btn-secondary flex-1" onClick={handleCancelInlineEdit}>
                                    Abbrechen
                                  </button>
                                </div>
                                <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                                  <button
                                    type="button"
                                    className="btn btn-secondary w-full text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-200"
                                    onClick={() => {
                                      if (window.confirm("Eintrag wirklich löschen?")) {
                                        handleDelete(entry.id);
                                        handleCancelInlineEdit();
                                      }
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="inline mr-2">
                                      <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M5.33301 4.00004V2.66671C5.33301 2.31309 5.47348 1.97395 5.72353 1.7239C5.97358 1.47385 6.31272 1.33337 6.66634 1.33337H9.33301C9.68663 1.33337 10.0258 1.47385 10.2758 1.7239C10.5259 1.97395 10.6663 2.31309 10.6663 2.66671V4.00004M12.6663 4.00004V13.3334C12.6663 13.687 12.5259 14.0261 12.2758 14.2762C12.0258 14.5262 11.6866 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2762C3.47348 14.0261 3.33301 13.687 3.33301 13.3334V4.00004H12.6663Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Eintrag löschen
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Desktop/Tablet: Inline Edit */}
                      <tr className="hidden md:table-row inline-edit-row">
                        <td colSpan={5} style={{ padding: 0 }}>
                          <div className="bg-secondary-50 dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 p-4 rounded-lg m-2 shadow-sm">
                            <form onSubmit={handleSaveInlineEdit}>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="form-group">
                                  <label className="label text-xs mb-1 font-semibold">Datum</label>
                                  <input
                                    type="date"
                                    value={inlineEditForm.date}
                                    onChange={(e) => setInlineEditForm({ ...inlineEditForm, date: e.target.value })}
                                    required
                                    className="input text-sm p-2"
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="label text-xs mb-1 font-semibold">Kategorie</label>
                                  <select
                                    value={inlineEditForm.category}
                                    onChange={(e) => setInlineEditForm({ ...inlineEditForm, category: e.target.value })}
                                    required
                                    className="input text-sm p-2"
                                  >
                                    <option value="">Wählen...</option>
                                    {categories.map((cat) => (
                                      <option key={cat.id} value={cat.name}>
                                        {cat.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label className="label text-xs mb-1 font-semibold">Wert</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={inlineEditForm.value}
                                    onChange={(e) => setInlineEditForm({ ...inlineEditForm, value: e.target.value })}
                                    required
                                    className="input text-sm p-2"
                                    placeholder="0.00"
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="label text-xs mb-1 font-semibold">Währung</label>
                                  <select
                                    value={inlineEditForm.currency}
                                    onChange={(e) => setInlineEditForm({ ...inlineEditForm, currency: e.target.value })}
                                    required
                                    className="input text-sm p-2"
                                  >
                                    <option value="">Wählen...</option>
                                    {currencies.map((cur) => (
                                      <option key={cur.id} value={cur.code}>
                                        {cur.code}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="form-group md:col-span-2">
                                  <label className="label text-xs mb-1 font-semibold">Notizen (optional)</label>
                                  <textarea
                                    value={inlineEditForm.notes || ""}
                                    onChange={(e) => setInlineEditForm({ ...inlineEditForm, notes: e.target.value })}
                                    rows="2"
                                    className="input text-sm p-2 resize-y min-h-[50px]"
                                    placeholder="Notizen..."
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2 mt-3">
                                <button type="submit" className="btn btn-primary flex-1 text-sm">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="inline mr-1">
                                    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  Speichern
                                </button>
                                <button type="button" className="btn btn-secondary flex-1 text-sm" onClick={handleCancelInlineEdit}>
                                  Abbrechen
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary text-sm text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-200"
                                  onClick={() => {
                                    if (window.confirm("Eintrag wirklich löschen?")) {
                                      handleDelete(entry.id);
                                      handleCancelInlineEdit();
                                    }
                                  }}
                                  title="Eintrag löschen"
                                >
                                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="inline">
                                    <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M5.33301 4.00004V2.66671C5.33301 2.31309 5.47348 1.97395 5.72353 1.7239C5.97358 1.47385 6.31272 1.33337 6.66634 1.33337H9.33301C9.68663 1.33337 10.0258 1.47385 10.2758 1.7239C10.5259 1.97395 10.6663 2.31309 10.6663 2.66671V4.00004M12.6663 4.00004V13.3334C12.6663 13.687 12.5259 14.0261 12.2758 14.2762C12.0258 14.5262 11.6866 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2762C3.47348 14.0261 3.33301 13.687 3.33301 13.3334V4.00004H12.6663Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              </div>
                            </form>
                          </div>
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr
                      className="table-row-actions table-row-clickable"
                      onClick={() => handleInlineEditClick(entry)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{entry.date}</td>
                      <td>{entry.category}</td>
                      <td>
                        {Number(entry.value).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td>{entry.currency}</td>
                      <td>{entry.notes}</td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}

export default Input;
